import classnames from 'classnames';
import { useMemo } from 'react';
import { Button } from '@sorrem-client/ui/components';
import { withSkillHotkey, withoutHotkey } from '@sorrem-client/ui/shared/action-bar';
import { hasSkillTreeSkillIcon, SkillTreeSkillIcon } from '@sorrem-client/ui/features/SkillTree';
import { useActionBarKeyLabels, useActionBarTargets } from '@sorrem-client/ui/hooks';
import { formatEssenceCost } from '@sorrem-client/ui/features/ActionSlotSummary/ability-facts';
import { resolveAbilityEssenceCost, type CharacterEquipmentLoadout, type Skill } from '@sorrem/shared';
import { toAbilityPickerGroups } from './ability-picker-groups';
import styles from './AbilityPicker.module.scss';

interface AbilityPickerProps {
    /** The action slot a pick goes to (zero-based). */
    hotkeyIndex: number;
    /** Called once a pick or a clear has been made. */
    onDone: () => void;
    /** The character's skills and gear, so each ability shows what it costs them. */
    skills?: readonly Skill[];
    equipped?: CharacterEquipmentLoadout;
}

/**
 * The list a right-clicked action slot opens: every Essence ability, under
 * its essence type, with its cost and the key it is on now. Picking one puts it
 * on the slot (moving it off any other key, and replacing what the slot
 * held); "Clear slot" empties it.
 */
export function AbilityPicker({ hotkeyIndex, onDone, skills, equipped }: AbilityPickerProps): JSX.Element {
    const { targets, setTargets } = useActionBarTargets();
    const groups = useMemo(() => toAbilityPickerGroups(), []);
    const actionKeyLabels = useActionBarKeyLabels();
    const keyLabel = (index: number): string => actionKeyLabels[index] ?? '';
    const current = targets[hotkeyIndex] ?? null;

    const pick = (skillId: string): void => {
        setTargets(withSkillHotkey(targets, hotkeyIndex, skillId));
        onDone();
    };
    const clear = (): void => {
        setTargets(withoutHotkey(targets, hotkeyIndex));
        onDone();
    };

    return (
        <section className={styles.abilityPicker} aria-label={`Choose an ability for action ${hotkeyIndex + 1}`}>
            <h3 className={styles.title}>Action {keyLabel(hotkeyIndex)}</h3>
            <div className={styles.groups}>
                {groups.map((group) => (
                    <div key={group.essenceType} className={styles.group}>
                        <h4 className={styles.groupLabel}>{group.label}</h4>
                        {group.abilities.map((ability) => {
                            const isCurrent = current?.kind === 'skill' && current.skillId === ability.skillId;
                            const onIndex = targets.findIndex((target) => target?.kind === 'skill' && target.skillId === ability.skillId);
                            return (
                                // A list row, not a styled control: no project component lays out like one.
                                <button
                                    key={ability.id}
                                    type='button'
                                    className={classnames(styles.row, isCurrent && styles.current)}
                                    aria-pressed={isCurrent}
                                    title={ability.description}
                                    onClick={() => pick(ability.skillId)}
                                >
                                    <span className={styles.icon} aria-hidden='true'>
                                        {hasSkillTreeSkillIcon(ability.skillId) && <SkillTreeSkillIcon skillId={ability.skillId} />}
                                    </span>
                                    <span className={styles.name}>{ability.name}</span>
                                    <span className={styles.cost}>{formatEssenceCost(resolveAbilityEssenceCost(ability, skills, equipped))}</span>
                                    <span className={styles.key}>{onIndex >= 0 ? keyLabel(onIndex) : ''}</span>
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>
            <Button variant='link' size='small' className={styles.clear} disabled={current === null} onClick={clear}>
                Clear slot
            </Button>
        </section>
    );
}
